import { OrderingUmbrellaSelectOption } from "../../components/ordering-check-card/types";
import { getBundleCheckTypesFromBundle } from "./bundle-conflict-utils";
import {
  Package,
  SelectedBundlesType,
  SelectedChecksType,
} from "./package-creation/PackageContext";
import { getCheckPrice } from "./package-creation/package-utils";
import { BundleData, CheckData, PackageDetailResponse } from "./types";

type PackageCurrency = NonNullable<PackageDetailResponse["price"]>["currency"];

function mapSelectedOptions(
  packageCheck: PackageDetailResponse["checks"][number],
  availableCheck?: CheckData
): OrderingUmbrellaSelectOption[] | undefined {
  const getMatchingCatalogOption = (
    selectedOption: OrderingUmbrellaSelectOption
  ) =>
    availableCheck?.select_options?.find((catalogOption) => {
      const selectedValue = Array.isArray(selectedOption.value)
        ? selectedOption.value
        : [selectedOption.value];
      const catalogValue = Array.isArray(catalogOption.value)
        ? catalogOption.value
        : [catalogOption.value];

      return JSON.stringify(selectedValue) === JSON.stringify(catalogValue);
    });

  if (packageCheck.selected_options?.length) {
    return packageCheck.selected_options.map((option) => {
      const selectOption = option as OrderingUmbrellaSelectOption;
      const catalogOption = getMatchingCatalogOption(selectOption);
      return {
        ...catalogOption,
        ...selectOption,
        label: selectOption.label ?? catalogOption?.label ?? selectOption.value,
      };
    });
  }

  if (!packageCheck.selected_option) {
    return undefined;
  }

  const selectedOption =
    packageCheck.selected_option as OrderingUmbrellaSelectOption;
  const catalogOption = getMatchingCatalogOption(selectedOption);
  return [
    {
      ...catalogOption,
      ...selectedOption,
      label:
        selectedOption.label ?? catalogOption?.label ?? selectedOption.value,
    },
  ];
}

function packageCheckToCheckDetails(
  packageCheck: PackageDetailResponse["checks"][number],
  packageCurrency: PackageCurrency,
  availableCheck?: CheckData
): CheckData {
  return {
    type: packageCheck.type,
    name: packageCheck.name,
    price: packageCheck.price,
    currency: packageCheck.currency ?? packageCurrency,
    estimated_turnaround_time: packageCheck.estimated_turnaround_time,
    disclaimers: packageCheck.disclaimers,
    description: packageCheck.description,
    info_paragraphs: packageCheck.info_paragraphs,
    key_features: packageCheck.key_features,
    coverage: packageCheck.coverage,
    how_it_can_help_you: packageCheck.how_it_can_help_you,
    select_field_descriptions: [],
    select_field_endpoint: "",
    select_field_information: undefined,
    umbrella_child_checks_heading: packageCheck.umbrella_child_checks_heading,
    check_has_ordering_error_for_customer_group:
      packageCheck.check_has_ordering_error_for_customer_group,
    mutually_exclusive_check_types:
      packageCheck.mutually_exclusive_check_types || [],
    required_check_types: availableCheck?.required_check_types ?? [],
    must_be_ordered_with_one_of:
      availableCheck?.must_be_ordered_with_one_of ?? [],
    select_options: availableCheck?.select_options ?? [],
    children: availableCheck?.children ?? [],
    add_on_options: packageCheck.add_on_options,
  } as unknown as CheckData;
}

function mapPackageCheckToSelectedCheck(
  packageCheck: PackageDetailResponse["checks"][number],
  packageCurrency: PackageCurrency,
  availableCheck?: CheckData
): SelectedChecksType {
  const checkDetails = packageCheckToCheckDetails(
    packageCheck,
    packageCurrency,
    availableCheck
  );
  const selectedOptions = mapSelectedOptions(packageCheck, availableCheck);

  return {
    key: packageCheck.type,
    name: packageCheck.name,
    check_arguments: packageCheck.check_arguments || {},
    cost: packageCheck.price ?? 0,
    currency: (packageCheck.currency ??
      packageCurrency) as SelectedChecksType["currency"],
    children: checkDetails.children,
    required_check_types: checkDetails.required_check_types,
    must_be_ordered_with_one_of: checkDetails.must_be_ordered_with_one_of,
    checkDetails,
    selectedOptions,
    add_on_options: checkDetails.add_on_options,
  };
}

function mapAvailableBundleToSelectedBundle(
  bundle: BundleData
): SelectedBundlesType {
  return {
    id: bundle.id,
    title: bundle.title,
    products: bundle.products,
    currency: bundle.currency,
    total: bundle.total,
    price: bundle.total?.toString(),
    check_types_with_arguments: bundle.check_types_with_arguments,
  };
}

function createPlaceholderSelectedBundle(
  bundleId: string,
  currency: SelectedBundlesType["currency"]
): SelectedBundlesType {
  return {
    id: bundleId,
    title: bundleId,
    products: [],
    currency,
    total: 0,
    check_types_with_arguments: {},
  };
}

export function getBundleCheckTypesForPackage(
  bundleIds: string[],
  availableBundles: BundleData[] = []
): Set<string> {
  const catalogById = new Map(
    availableBundles.map((bundle) => [String(bundle.id), bundle])
  );

  return new Set(
    bundleIds.flatMap((bundleId) => {
      const bundle = catalogById.get(String(bundleId));
      return bundle ? getBundleCheckTypesFromBundle(bundle) : [];
    })
  );
}

export function filterPackageChecksExcludingBundles(
  checks: PackageDetailResponse["checks"],
  bundleIds: string[],
  availableBundles: BundleData[] = []
): PackageDetailResponse["checks"] {
  const bundleCheckTypes = getBundleCheckTypesForPackage(
    bundleIds,
    availableBundles
  );

  return checks.filter((check) => !bundleCheckTypes.has(check.type));
}

export function packageDetailToPackageContext(
  packageDetail: PackageDetailResponse,
  availableBundles: BundleData[] = [],
  availableChecks: CheckData[] = []
): Package {
  const packageCurrency = (packageDetail.price?.currency ??
    "CAD") as PackageCurrency;
  const packageBundleIds = [...(packageDetail.bundles || [])].map(String);
  const catalogById = new Map(
    availableBundles.map((bundle) => [String(bundle.id), bundle])
  );

  const selectedBundles = packageBundleIds.map((bundleId) => {
    const bundle = catalogById.get(bundleId);
    if (bundle) {
      return mapAvailableBundleToSelectedBundle(bundle);
    }

    return createPlaceholderSelectedBundle(
      bundleId,
      packageCurrency as SelectedBundlesType["currency"]
    );
  });

  const bundleCheckTypes = new Set(
    selectedBundles.flatMap((bundle) => getBundleCheckTypesFromBundle(bundle))
  );
  const availableChecksByType = new Map(
    availableChecks.map((check) => [check.type, check])
  );

  const checks = packageDetail.checks
    .filter((packageCheck) => !bundleCheckTypes.has(packageCheck.type))
    .map((packageCheck) =>
      mapPackageCheckToSelectedCheck(
        packageCheck,
        packageCurrency,
        availableChecksByType.get(packageCheck.type)
      )
    );

  return {
    name: packageDetail.name,
    description: packageDetail.description,
    tag_names: packageDetail.tags,
    checks,
    bundles: selectedBundles,
    rubric_id: packageDetail.adjudication_rubric?.id ?? null,
    is_ats_package: packageDetail.is_ats_package ?? false,
    is_api_package: packageDetail.is_api_package ?? false,
    client_input_claims: packageDetail.client_input_claims ?? {},
  };
}

export function availableCheckToSelectedCheck(
  check: CheckData
): SelectedChecksType {
  const firstOption = check.select_options?.[0] as
    | OrderingUmbrellaSelectOption
    | undefined;

  return {
    key: check.type,
    name: check.name,
    check_arguments: {},
    cost: getCheckPrice(check, firstOption),
    currency: check.currency,
    children: check.children,
    required_check_types: check.required_check_types,
    must_be_ordered_with_one_of: check.must_be_ordered_with_one_of,
    checkDetails: check,
    selectedOptions: firstOption ? [firstOption] : undefined,
    add_on_options: check.add_on_options?.map((addon) => ({
      key: addon.key,
      name: addon.name ?? addon.label,
      price: addon.price,
      currency: addon.currency,
    })),
  };
}
